import React, { useEffect, useState } from "react";
import { useSearchParams, useParams, Link, useNavigate } from "react-router-dom";
import StorefrontLayout from "../../components/storefront/StorefrontLayout";
import ProductCard from "../../components/storefront/ProductCard";
import Paginated from "../../components/Paginated";
import { api } from "../../lib/api";
import { ArrowRight } from "lucide-react";
import BudgetMatcherBar from "../../components/BudgetMatcherBar";
import { toast } from "sonner";

/**
 * Smart Budget Match — reachable from the storefront header (users, salespeople
 * and admins all use this same page). Enter a budget (₹) OR a weight (grams),
 * pick Gold/Silver, hit Match. Results come in category sections of 8 (4 × 2)
 * with a View More button that opens the category subpage.
 */
const priceOf = (p) => (typeof p.calculated_price === "number" ? p.calculated_price : p.price);

const buildQuery = ({ mode, value, metal }) => {
  const qp = new URLSearchParams();
  if (mode === "price") qp.set("budget", value); else qp.set("grams", value);
  qp.set("metal", metal);
  return qp;
};

export default function BudgetMatch() {
  const [sp, setSp] = useSearchParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const initial = { amount: sp.get("budget") || "", grams: sp.get("grams") || "", metal: sp.get("metal") || "Gold" };
  const hasQuery = sp.get("budget") || sp.get("grams");

  useEffect(() => {
    if (!hasQuery) { setData(null); return; }
    setLoading(true);
    api.get(`/api/budget-matcher?${sp.toString()}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Match failed"))
      .finally(() => setLoading(false));
  }, [sp]); // eslint-disable-line

  const onMatch = (q) => setSp(buildQuery(q));

  return (
    <StorefrontLayout>
      {/* Premium matcher banner — end to end */}
      <BudgetMatcherBar compact onMatch={onMatch} initial={{ value: initial.amount || initial.grams, grams: initial.grams, metal: initial.metal }} />
      <div className="px-6 lg:px-12 py-10 max-w-7xl mx-auto">
        {loading && <div className="py-16 text-center text-ash">Matching…</div>}

        {data && !loading && (
          <div className="mt-8 space-y-10">
            <div className="text-sm text-ash">{data.total_matched} piece(s) matched {data.budget ? `within ₹${(+data.budget).toLocaleString("en-IN")}` : `around ${data.grams}g`} in {data.metal || "all metals"}.</div>
            {Object.entries(data.groups || {}).map(([cat, items]) => (
              <section key={cat} data-testid={`bm-section-${cat}`}>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-display text-3xl text-maroon">{cat}</h2>
                  {items.length > 8 && (
                    <button onClick={() => navigate(`/budget-match/${encodeURIComponent(cat)}?${sp.toString()}`)} className="btn-outline text-xs" data-testid={`bm-viewmore-${cat}`}>
                      View More ({items.length}) <ArrowRight size={12}/>
                    </button>
                  )}
                </div>
                {/* 8 items — 4 in the first row, 4 in the second */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  {items.slice(0, 8).map((p) => <ProductCard key={p.id} product={p} price={priceOf(p)}/>)}
                </div>
              </section>
            ))}
            {data.total_matched === 0 && (
              <div className="py-16 text-center text-ash">No pieces matched. Try a higher budget or different weight.</div>
            )}
          </div>
        )}
      </div>
    </StorefrontLayout>
  );
}

/** Subpage: all matched items for one category, paginated. */
export function BudgetMatchCategory() {
  const { category } = useParams();
  const [sp] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/api/budget-matcher?${sp.toString()}`)
      .then((r) => setItems((r.data?.groups || {})[category] || []))
      .finally(() => setLoading(false));
  }, [sp, category]); // eslint-disable-line

  return (
    <StorefrontLayout>
      <div className="px-6 lg:px-12 py-10 max-w-7xl mx-auto">
        <Link to={`/budget-match?${sp.toString()}`} className="text-xs text-maroon underline" data-testid="bm-back">← Back to Smart Budget Match</Link>
        <h1 className="font-display text-4xl text-maroon mt-2">{category} — matched to your budget</h1>
        <p className="text-sm text-ash mt-1 mb-6">{items.length} piece(s) filtered by Smart Budget Match.</p>
        {loading ? <div className="py-16 text-center text-ash">Loading…</div> : (
          <Paginated items={items} pageSize={16}>
            {(pageItems) => (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {pageItems.map((p) => <ProductCard key={p.id} product={p} price={priceOf(p)}/>)}
              </div>
            )}
          </Paginated>
        )}
      </div>
    </StorefrontLayout>
  );
}
